import React from 'react';
import { Lock, Sparkles, ShieldCheck, ArrowRight, CheckCircle2, Zap, BarChart3, Bot } from 'lucide-react';
import { checkSubscriptionLimits, getSubscriptionData } from '../utils/subscriptionManager';
import { getUserTrialStatus } from '../utils/userManagement';

export interface PaywallGuardProps {
  /** Type of feature or resource being guarded */
  feature: 'ai_query' | 'invoice' | 'expense' | 'booking' | 'analytics' | 'advanced_feature';
  /** Display title for the guarded feature */
  featureName?: string;
  /** Custom description explaining why access is restricted */
  featureDescription?: string;
  /** Current item count for quantitative limits (e.g. AI queries used today or logged invoices) */
  currentCount?: number;
  /** Optional user email to evaluate 7-day free trial status */
  currentUserEmail?: string;
  /** Force Pro/Enterprise tier requirement regardless of numerical count */
  requireProTierOnly?: boolean;
  /** Navigation callback to direct user to settings or billing tab */
  onNavigate?: (path: string) => void;
  /** Optional custom trigger when Upgrade CTA is clicked */
  onUpgrade?: () => void;
  /** Render mode: 'replace' hides children completely, 'overlay' blurs children behind paywall card */
  mode?: 'replace' | 'overlay';
  /** Children elements to protect */
  children: React.ReactNode;
}

/**
  * PaywallGuard wrapper component
  * Intercepts user access to sensitive features like 'AI Co-Founder' or 'Advanced Analytics'.
  * Evaluates subscription limits via `checkSubscriptionLimits` and `getSubscriptionData`.
  * Blocks access with an 'Upgrade to Pro' CTA if the user is on the Free tier or quota is exceeded.
  */
export const PaywallGuard: React.FC<PaywallGuardProps> = ({
  feature,
  featureName,
  featureDescription,
  currentCount,
  currentUserEmail,
  requireProTierOnly = false,
  onNavigate,
  onUpgrade,
  mode = 'replace',
  children
}) => {
  const [subData, setSubData] = React.useState(() => getSubscriptionData());

  React.useEffect(() => {
    const handleSubChange = () => {
      setSubData(getSubscriptionData());
    };
    window.addEventListener('subscription_data_updated', handleSubChange);
    window.addEventListener('storage', handleSubChange);
    return () => {
      window.removeEventListener('subscription_data_updated', handleSubChange);
      window.removeEventListener('storage', handleSubChange);
    };
  }, []);

  const planId = subData.currentPlanId || 'starter';
  const trial = getUserTrialStatus(currentUserEmail);
  const isTrialActive = trial.isTrialActive && !trial.isTrialExpired;
  const isOwnerOrSubscribed = trial.isOwner || trial.isSubscribed || (subData.status === 'active' && !!subData.billingEmail);
  const isTrialOrPaid = isOwnerOrSubscribed || isTrialActive || subData.status === 'trialing' || planId === 'pro' || planId === 'enterprise';

  // Evaluate access rights
  let isAllowed = isTrialOrPaid;
  let limitMessage: string | undefined;

  if (requireProTierOnly || feature === 'analytics' || feature === 'advanced_feature') {
    isAllowed = isTrialOrPaid;
    if (!isAllowed) {
      limitMessage = `${featureName || 'This feature'} is exclusively available on Sovereign Founder Pro and Enterprise plans. Upgrade to unlock full access.`;
    }
  } else {
    // Quantitative limit check for ai_query, invoice, expense, booking
    const check = checkSubscriptionLimits(feature, currentCount ?? (feature === 'ai_query' ? 0 : 10), currentUserEmail);
    isAllowed = check.allowed;
    limitMessage = check.message;
  }

  // If user has valid tier or quota, render children uninterrupted
  if (isAllowed) {
    return <>{children}</>;
  }

  const handleUpgradeClick = () => {
    if (onUpgrade) {
      onUpgrade();
    } else if (onNavigate) {
      onNavigate('/settings');
    } else {
      window.location.hash = '#/settings';
    }
  };

  const resolvedTitle = featureName || (
    feature === 'ai_query' ? 'AI Co-Founder Strategy Engine' :
    feature === 'analytics' ? 'Advanced Cash Flow Analytics' :
    feature === 'advanced_feature' ? 'Sovereign HQ Power Tools' :
    'Sovereign Founder Pro Feature'
  );

  const resolvedDescription = featureDescription || limitMessage || (
    feature === 'ai_query'
      ? 'The AI Co-Founder runs strictly on a sovereign Bring-Your-Own-Key (BYOK) model. Connect your free Google AI Studio key once opted in for direct, unmetered CFO insights.'
      : feature === 'analytics'
      ? 'Unlock deep cash flow forecasting, profit margin analysis, stream performance breakdowns, and automated multi-currency tax estimates.'
      : 'Upgrade to Sovereign Founder Pro for unlimited client invoices, bookings, expenses, whitelisted multi-seats, and complete multi-currency tax engine access.'
  );

  const getFeatureIcon = () => {
    switch (feature) {
      case 'ai_query':
        return <Bot size={28} className="text-[#14B8A6]" />;
      case 'analytics':
        return <BarChart3 size={28} className="text-[#14B8A6]" />;
      default:
        return <Zap size={28} className="text-[#14B8A6]" />;
    }
  };

  const proFeatures = [
    'Unlimited Invoices, Expenses & Bookings',
    'AI Co-Founder Strategic Engine (Strictly BYOK)',
    'Multi-Currency & Automated VAT Tax Engine',
    'Real-time Cash Flow & Margin Forecasting',
    'AES-256 Cloud Crypto Vault Sync',
    'Automated Audit Logging & Direct PDF Exports'
  ];

  const paywallCard = (
    <div className="bg-[#151819] border-2 border-[#14B8A6]/40 rounded-3xl p-6 sm:p-8 max-w-2xl mx-auto shadow-2xl relative overflow-hidden text-left space-y-6 animate-fade-in">
      {/* Background Glow Effect */}
      <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#14B8A6]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header Badge & Title */}
      <div className="space-y-3 relative z-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="px-3 py-1 bg-[#14B8A6]/15 text-[#14B8A6] border border-[#14B8A6]/30 font-extrabold text-[11px] uppercase tracking-wider rounded-xl flex items-center gap-1.5 shadow-sm">
            <Lock size={13} />
            <span>Pro Subscription Required</span>
          </span>
          <span className="text-xs text-gray-400 font-medium">
            Current Tier: <strong className="text-amber-400 uppercase">{planId}</strong>
          </span>
        </div>

        <div className="flex items-start gap-3.5 pt-1">
          <div className="p-3 bg-[#14B8A6]/10 border border-[#14B8A6]/20 rounded-2xl shrink-0">
            {getFeatureIcon()}
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight flex items-center gap-2">
              <span>{resolvedTitle}</span>
            </h2>
            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed mt-1">
              {resolvedDescription}
            </p>
          </div>
        </div>
      </div>

      {/* Feature Bullet Points */}
      <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl p-4 sm:p-5 space-y-2.5 relative z-10">
        <span className="text-[11px] uppercase tracking-wider font-extrabold text-gray-400 block mb-1">
          Included in Sovereign Founder Pro:
        </span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-200">
          {proFeatures.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <CheckCircle2 size={15} className="text-[#14B8A6] shrink-0" />
              <span className="font-medium">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Button & Actions */}
      <div className="space-y-3 pt-2 relative z-10">
        <button
          type="button"
          onClick={handleUpgradeClick}
          className="w-full py-3.5 px-6 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-sm rounded-xl shadow-xl shadow-[#14B8A6]/25 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer active:scale-[0.99]"
        >
          <Sparkles size={18} />
          <span>Upgrade to Sovereign Founder Pro</span>
          <ArrowRight size={16} />
        </button>

        {/* 14-Day Refund Guarantee Footer Notice */}
        <div className="p-3 bg-[#0C0E0F]/80 border border-[#14B8A6]/25 rounded-xl text-[11px] text-gray-300 text-center flex items-center justify-center gap-2 font-medium">
          <ShieldCheck size={15} className="text-[#14B8A6] shrink-0" />
          <span>Try it first. If it’s not for you, you have 14 days to request a full refund. After that, cancel anytime.</span>
        </div>
      </div>
    </div>
  );

  if (mode === 'overlay') {
    return (
      <div className="relative min-h-[300px]">
        {/* Blurred Children */}
        <div className="filter blur-md opacity-30 pointer-events-none select-none aria-hidden">
          {children}
        </div>
        {/* Overlay Paywall Card */}
        <div className="absolute inset-0 flex items-center justify-center p-4 z-20 overflow-y-auto">
          {paywallCard}
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 flex items-center justify-center min-h-[400px]">
      {paywallCard}
    </div>
  );
};
